import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import type {
  TableColumn,
  TableColumnConfig,
  TableColumnWidths,
} from '@ipfabric/utils';
import { arrayToRecord } from '@ipfabric/utils/fp/arrayToRecord';
import { is } from '@ipfabric/utils/fp/comparison';
import { between } from '@ipfabric/utils/fp/number';
import { getPrimitiveOrd } from '@ipfabric/utils/fp/ordering';
import { prop } from '@ipfabric/utils/fp/prop';
import { getColumnDefaultWidth } from 'components/Table/helpers/getColumnWidth';
import type { TableCommandFactory } from 'components/Table/model/TableModel';
import { getColumnWidthThatFitsContent } from 'components/Table/model/commands/helpers/getColumnWidthThatFitsContent';
import { selectVisibleColumns } from 'components/Table/model/selectors/selectVisibleColumns';
import {
  TABLE_COLUMN_MIN_WIDTH,
  TABLE_CONTENT_BORDER_WIDTH,
  TABLE_RIGHT_HIDDEN_WIDTH,
} from 'components/Table/tableSettings';
import { array, ord, record } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { produce } from 'immer';
import { sum } from 'utils/number/sum';
import { getFilteredColumnMinWidth } from './helpers/getFilteredColumnMinWidth';

export const MIN_COLUMN_AUTO_SIZE = TABLE_COLUMN_MIN_WIDTH;
const MAX_COLUMN_AUTO_SIZE = 500;

/**
 * Header needs this space around title so that the title fully renders.
 *
 * To simplify implementation, let's just reserve space to all columns
 * since they will be stretched by header size only if all content was fit and there is still horizontal
 * space left, so we can afford to splurge on horizontal space a bit.
 */
const HEADER_EXTRA_SPACE = 36;

const getHeaderWidthThatFitsContent =
  (container: HTMLElement) => (column: TableColumnConfig) => {
    const headerCellTitle = container.querySelector(
      `[data-column-header="${CSS.escape(column.key)}"] .ipf-table-header-cell__title`,
    );
    if (headerCellTitle === null) {
      return 0;
    }
    return headerCellTitle.scrollWidth + HEADER_EXTRA_SPACE;
  };

const getFilterWidth = (column: TableColumnConfig) =>
  column.filter ? getFilteredColumnMinWidth(column.filter) : 0;

const orderByOverflowSize = pipe(
  // ORDER BY overflowSize ASC
  getPrimitiveOrd(),
  ord.contramap<number, { overflowSize: number }>(prop('overflowSize')),
);

/**
 * Remaining space is divided using this algorithm:
 *
 * 1. Go from columns that are overflowed by least amount
 * 2. If there is enough space to stretch them to show full content by giving the same amount of space
 *      to all columns, do it & repeat step 1.
 * 3. Otherwise, distribute remaining space evenly (+ distribute integer division so that table exactly fits viewport).
 */
const distributeRemainingSpaceToWideColumns = (
  availableWidth: number,
  measuredWidths: TableColumnWidths,
): ((cappedWidths: TableColumnWidths) => TableColumnWidths) =>
  produce<TableColumnWidths>((draftCappedWidths) => {
    let remainingAvailableWidth =
      availableWidth - sum(Object.values(draftCappedWidths));
    if (remainingAvailableWidth <= 0) {
      return;
    }
    const overflowsFromSmallest = pipe(
      Object.keys(measuredWidths),
      array.map((key) => ({
        key,
        // Fixed noUncheckedIndexedAccess NIM-19408
        overflowSize: measuredWidths[key]! - draftCappedWidths[key]!,
      })),
      array.filter((x) => x.overflowSize > 0),
      array.sort(orderByOverflowSize),
    );
    for (
      let index = 0;
      remainingAvailableWidth > 0 && index < overflowsFromSmallest.length;
      index += 1
    ) {
      // Fixed noUncheckedIndexedAccess NIM-19408
      const { overflowSize, key } = overflowsFromSmallest[index]!;
      const remainingColumnsToProcess = overflowsFromSmallest.length - index;
      const spaceToGive = Math.min(
        // If there is enough space to add this space to all remaining columns, saturate this one fully.
        overflowSize,
        // Otherwise, distribute remaining space evenly.
        // Note that doing it this way step by step automatically solves distribution of remainder of integer division.
        Math.floor(remainingAvailableWidth / remainingColumnsToProcess),
      );
      remainingAvailableWidth -= spaceToGive;
      // Fixed noUncheckedIndexedAccess NIM-19408
      draftCappedWidths[key]! += spaceToGive;
    }
  });

export const autoSizeTableColumns: TableCommandFactory = (cmdContext) => () => {
  const tableContainer =
    cmdContext.getState().tableContainerRef?.current ?? null;
  const { tableContainerSize, simpleFilterFields } = cmdContext.getState();
  if (tableContainer === null) {
    stumble('Cannot auto-size table, missing wrapper element reference');
    return;
  }
  if ((tableContainerSize?.width ?? 0) === 0) {
    // table is probably invisible, skip and keep `requestedAutoSize` true. Auto size will happen
    // once the table is back on display.
    return;
  }
  cmdContext.updateState((draft) => {
    const visibleColumns = selectVisibleColumns(cmdContext.getState());
    const { left: dynamicWidthColumns, right: fixedWidthColumns } =
      array.partition<TableColumn<any>>((c) => !!c.fixedWidth)(visibleColumns);

    const measuredWidths: TableColumnWidths = pipe(
      dynamicWidthColumns,
      arrayToRecord(
        prop('key'),
        flow(prop('key'), getColumnWidthThatFitsContent(tableContainer)),
      ),
    );

    if (Object.values(measuredWidths).every(is(Number.NEGATIVE_INFINITY))) {
      // no column rendered yet
      return;
    }

    const measuredHeaderWidths: Record<string, number> = pipe(
      dynamicWidthColumns,
      arrayToRecord(prop('key'), getHeaderWidthThatFitsContent(tableContainer)),
    );

    const filterWidths: Record<string, number> = pipe(
      dynamicWidthColumns,
      arrayToRecord(prop('key'), getFilterWidth),
    );

    const fixedWidthTotal = pipe(
      fixedWidthColumns,
      array.map(getColumnDefaultWidth),
      sum,
    );

    const getMinColumnWidth = (key: string): number => {
      if (simpleFilterFields?.[key]?.value) {
        return getFilteredColumnMinWidth(simpleFilterFields?.[key]?.filter);
      }
      return MIN_COLUMN_AUTO_SIZE;
    };

    // Old: There is TABLE_CONTENT_BORDER_WIDTH on both sides so it should be subtracted from available width twice, but
    // there is added correction of marginRight -TABLE_CONTENT_BORDER_WIDTH which hides double border on the right side.
    // This kind of border is currently unused, see comment of TABLE_CONTENT_BORDER_WIDTH.
    // New: TABLE_RIGHT_HIDDEN_WIDTH is added to the right side of the table to hide the right border of the last column.
    // Correction is done in TableView.tsx file.
    const dynamicColumnsAvailableWidth =
      (tableContainerSize?.width ?? 0) -
      fixedWidthTotal +
      TABLE_CONTENT_BORDER_WIDTH +
      TABLE_RIGHT_HIDDEN_WIDTH;

    draft.settings.columnWidth = pipe(
      measuredWidths,
      record.mapWithIndex((key, value) => {
        return between(getMinColumnWidth(key), MAX_COLUMN_AUTO_SIZE)(value);
      }),
      distributeRemainingSpaceToWideColumns(
        dynamicColumnsAvailableWidth,
        measuredWidths,
      ),
      distributeRemainingSpaceToWideColumns(
        dynamicColumnsAvailableWidth,
        measuredHeaderWidths,
      ),
      distributeRemainingSpaceToWideColumns(
        dynamicColumnsAvailableWidth,
        filterWidths,
      ),
    );
    draft.requestedAutoSize = false;
  });
};

export const testable = {
  distributeRemainingSpaceToWideColumns,
};
